import { useTranslations } from '@/i18n/translations';
import { Button, Label, Pressable, Row } from '@leanin/ui';
import { totalReactions, type ReactionCounts, type ReactionKind } from '@leanin/shared/reactions';
import { ReactionStack } from './ReactionPicker';

type Props = {
  counts: ReactionCounts;
  mine: ReactionKind | null;
} & (
  // `pill` rides a comment bubble's corner (the bubble positions it).
  | { variant: 'pill'; onOpenRoster: () => void }
  // `row` sits at the end of a post's or article's action row. Omit
  // onOpenRoster where no roster exists (articles): the summary is then
  // read-only.
  | { variant: 'row'; onOpenRoster?: () => void }
);

// What everyone did: the top reaction icons and the total count, opening the
// who-reacted roster. ReactionButton shows what the viewer did. Renders
// nothing until someone has reacted.
export function ReactionSummary(props: Props) {
  const { counts, mine } = props;
  const t = useTranslations('Feed');
  const total = totalReactions(counts);
  if (total === 0) return null;

  const content =
    props.variant === 'pill' ? (
      <>
        <ReactionStack counts={counts} mine={mine} size={16} max={2} />
        <Label size="sm">{total}</Label>
      </>
    ) : (
      <Row gap="xs" align="center">
        <ReactionStack counts={counts} mine={mine} size={18} />
        <Label size="sm" color="mute">
          {total}
        </Label>
      </Row>
    );

  if (props.variant === 'pill') {
    return (
      <Pressable
        onClick={props.onOpenRoster}
        aria-label={t('reactions.seeWhoAria')}
        style={{
          // Lifted off the bubble fill by a soft shadow — the one place the
          // design uses one, so no shadow token exists for it.
          // eslint-disable-next-line @leanin/ui/no-raw-shadow-literals -- every --shadow-* token is `none`; this design-approved lift has no token to use
          boxShadow: '0 0.0625rem 0.25rem rgba(0, 0, 0, 0.08)',
          display: 'inline-flex',
          alignItems: 'center',
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- Pressable is not a layout primitive; no gap prop exists on it
          gap: 'var(--space-xs)',
          // 0.125rem vertical is an optical fit around the 1rem discs; the
          // disc side gets less inline padding than the number so both read
          // centered inside the pill (mirrors the approved mockup).
          // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- asymmetric optical padding (0.125rem vertical) is not expressible as SpaceKey props
          padding: '0.125rem var(--space-sm) 0.125rem var(--space-xs)',
          background: 'var(--card)',
          border: 'var(--border-hairline) solid var(--border)',
          borderRadius: 'var(--radius-full)',
          cursor: 'pointer',
        }}
      >
        {content}
      </Pressable>
    );
  }

  if (props.onOpenRoster === undefined) return content;

  return (
    <Button
      variant="ghost"
      size="sm"
      padX="sm"
      // The discs sit at the row's inset, mirroring the React icon at the
      // other end.
      flush="end"
      aria-label={t('reactions.seeWhoAria')}
      onClick={props.onOpenRoster}
    >
      {content}
    </Button>
  );
}
